import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Button,
  useTheme,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MdPendingActions, MdEventNote, MdReceiptLong, MdArrowForward } from "react-icons/md";
import usePermissions from "hooks/usePermissions";
import { selectCurrentUser } from "store/features/auth/authSelectors";

const PendingApprovalsWidget = ({
  userRole,
  leaves = [],
  requisitions = [],
  loading = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";
  const { hasPermission, permissions } = usePermissions();
  const currentUser = useSelector(selectCurrentUser);

  const isEmployeeRole = userRole?.toLowerCase() === "employee";
  const isCompanyRole = userRole?.toLowerCase() === "company";

  // Permission checks for Leave widget display
  const canApproveLeave =
    hasPermission("leave_details.approve") ||
    hasPermission("leave_details.view") ||
    hasPermission("leave_details.edit") ||
    hasPermission("leave_details.action") ||
    isCompanyRole;

  const canApplyLeave =
    hasPermission("apply_leave.view") ||
    hasPermission("apply_leave.create") ||
    isEmployeeRole;

  const showLeaves = canApproveLeave || canApplyLeave;

  const showRequisitions =
    hasPermission("requisitions.view") ||
    hasPermission("requisition.view") ||
    hasPermission("requisition.approve") ||
    !isEmployeeRole;


  // Determine initial active tab: 0 for Leaves, 1 for Requisitions
  const initialTab = showLeaves ? 0 : 1;
  const [activeTab, setActiveTab] = useState(initialTab);

  // Filter leaves
  const pendingLeaves = (Array.isArray(leaves) ? leaves : []).filter((l) => {
    if (!l) return false;
    const s = String(l.status || l.approval_status || "").toLowerCase();
    return !s || s === "pending" || s === "submitted" || s === "in_review";
  });
  const displayLeaves = pendingLeaves.length > 0 ? pendingLeaves : (Array.isArray(leaves) ? leaves : []);

  // Filter requisitions
  const safeRequisitions = Array.isArray(requisitions) ? requisitions : [];
  const pendingRequisitions = safeRequisitions.filter((r) => {
    if (!r) return false;
    const s = String(r.status || r.approval_status || r.requisition_status || "").toLowerCase();
    return !s || s === "pending" || s === "submitted" || s === "in_review" || s === "open";
  });
  const displayRequisitions = pendingRequisitions.length > 0 ? pendingRequisitions : safeRequisitions;

  // Don't render anything if neither module is visible
  if (!showLeaves && !showRequisitions) {
    return null;
  }

  const renderLeavesList = () => (
    displayLeaves.length === 0 ? (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No leave applications found.
        </Typography>
      </Box>
    ) : (
      <Stack spacing={1.5}>
        {displayLeaves.slice(0, 3).map((leave, idx) => {
          const empName =
            leave.employee_name ||
            leave.applicant ||
            (leave.first_name ? `${leave.first_name} ${leave.last_name || ""}`.trim() : null) ||
            currentUser?.first_name ||
            currentUser?.name ||
            "My Request";
          const leaveType = leave.leave_type || leave.leave_type_name || leave.type_name || "Leave Application";
          const leaveStatus = leave.status || leave.approval_status || "Pending";
          const statusLower = String(leaveStatus).toLowerCase();

          return (
            <Box
              key={leave.id || idx}
              sx={{
                p: 1.8,
                borderRadius: 1,
                bgcolor: "background.paper",
                border: 1, borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: alpha("#ec4899", 0.15),
                    color: "#ec4899",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                  }}
                >
                  {(empName || "L")[0]?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                    {empName} • {leaveType}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {leave.from_date || leave.start_date || "-"} - {leave.to_date || leave.end_date || "-"}
                  </Typography>
                </Box>
              </Stack>

              <Chip
                label={leaveStatus}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  backgroundColor:
                    statusLower === "approved"
                      ? isDark ? alpha("#10b981", 0.2) : "#d1fae5"
                      : statusLower === "rejected"
                        ? isDark ? alpha("#ef4444", 0.2) : "#ffe4e6"
                        : isDark ? alpha("#f59e0b", 0.2) : "#fef3c7",
                  color:
                    statusLower === "approved"
                      ? "#059669"
                      : statusLower === "rejected"
                        ? "#dc2626"
                        : "#b45309",
                }}
              />
            </Box>
          );
        })}
      </Stack>
    )
  );

  const renderRequisitionsList = () => (
    displayRequisitions.length === 0 ? (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No requisitions found.
        </Typography>
      </Box>
    ) : (
      <Stack spacing={1.5}>
        {displayRequisitions.slice(0, 3).map((req, idx) => {
          const reqTitle =
            req.requisition_no ||
            req.requisition_type ||
            req.title ||
            req.item_name ||
            req.project_name ||
            req.description ||
            `Requisition #${req.id || idx + 1}`;

          const requestedBy =
            req.requested_by_name ||
            req.employee_name ||
            req.requested_by ||
            req.department_name ||
            "Staff";

          const reqStatus = req.status || req.approval_status || "Pending";
          const statusLower = String(reqStatus).toLowerCase();

          return (
            <Box
              key={req.id || idx}
              sx={{
                p: 1.8,
                borderRadius: 1,
                bgcolor: "background.paper",
                border: 1, borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor: alpha("#3b82f6", 0.15),
                    color: "#3b82f6",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                  }}
                >
                  {(reqTitle || "R")[0]?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                    {reqTitle}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Requested by: {requestedBy}
                  </Typography>
                </Box>
              </Stack>

              <Chip
                label={reqStatus}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  backgroundColor:
                    statusLower === "approved"
                      ? isDark ? alpha("#10b981", 0.2) : "#d1fae5"
                      : statusLower === "rejected"
                        ? isDark ? alpha("#ef4444", 0.2) : "#ffe4e6"
                        : isDark ? alpha("#f59e0b", 0.2) : "#fef3c7",
                  color:
                    statusLower === "approved"
                      ? "#059669"
                      : statusLower === "rejected"
                        ? "#dc2626"
                        : "#b45309",
                }}
              />
            </Box>
          );
        })}
      </Stack>
    )
  );

  return (
    <Paper
      component={motion.div}
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 1,
        background: isDark ? "#111827" : "#ffffff",
        border: 1, borderColor: "divider",
        boxShadow: isDark
          ? "0 4px 20px rgba(0, 0, 0, 0.25)"
          : "0 4px 20px rgba(15, 23, 42, 0.05)",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: isDark ? alpha("#ec4899", 0.2) : "#fce7f3",
                color: "#ec4899",
                display: "flex",
                alignItems: "center",
              }}
            >
              <MdPendingActions size={20} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary" }}>
              {canApproveLeave ? "Pending Approvals Queue" : "My Requests & Approvals"}
            </Typography>
          </Stack>
        </Stack>

        {/* Tab Headers if both modules are accessible */}
        {showLeaves && showRequisitions ? (
          <>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              variant="fullWidth"
              sx={{
                minHeight: 36,
                mb: 2,
                "& .MuiTab-root": {
                  minHeight: 36,
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#ec4899",
                },
              }}
            >
              <Tab
                icon={<MdEventNote size={16} />}
                iconPosition="start"
                label={canApproveLeave ? `Leaves (${displayLeaves.length})` : `My Leaves (${displayLeaves.length})`}
              />
              <Tab
                icon={<MdReceiptLong size={16} />}
                iconPosition="start"
                label={`Requisitions (${displayRequisitions.length})`}
              />
            </Tabs>

            {activeTab === 0 ? renderLeavesList() : renderRequisitionsList()}
          </>
        ) : showLeaves ? (
          renderLeavesList()
        ) : (
          renderRequisitionsList()
        )}
      </Box>

      {/* Footer Navigation Button */}
      <Button
        variant="outlined"
        fullWidth
        endIcon={<MdArrowForward />}
        onClick={() => {
          if (showLeaves && showRequisitions) {
            navigate(
              activeTab === 0
                ? (canApproveLeave ? "/company/leave-details" : "/company/apply-leave")
                : "/company/requisitions"
            );
          } else if (showLeaves) {
            navigate(canApproveLeave ? "/company/leave-details" : "/company/apply-leave");
          } else {
            navigate("/company/requisitions");
          }
        }}
        sx={{
          mt: 2,
          borderRadius: 1,
          textTransform: "none",
          fontWeight: 700,
          borderColor: isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1",
          color: isDark ? "#e2e8f0" : "#334155",
          "&:hover": {
            borderColor: "#ec4899",
            backgroundColor: alpha("#ec4899", 0.08),
          },
        }}
      >
        Manage {
          showLeaves && showRequisitions
            ? (activeTab === 0 ? (canApproveLeave ? "Leaves" : "My Leaves") : "Requisitions")
            : showLeaves
              ? (canApproveLeave ? "Leaves" : "My Leaves")
              : "Requisitions"
        }
      </Button>
    </Paper>
  );
};

export default PendingApprovalsWidget;
